import { groupEquipmentItemsByContractor, groupWorkforceItemsByContractor } from "@entities/daily-log";
import { type DailyLogFormValues, dailyLogFormSchema } from "@features/write-daily-log/model/schema";
import { z } from "zod";

const draftContentSchema = dailyLogFormSchema.partial().nullable();

export const dailyLogDraftSchema = z.object({
  id: z.number(),
  project: z.number(),
  user: z.number(),
  draft_id: z.string(),
  version: z.number().int().default(0),
  content: draftContentSchema.default({}),
  updated_at: z.string().nullable().optional(),
});

export const draftSaveRequestSchema = z.object({
  draft_id: z.string(),
  version: z.number().int(),
  content: draftContentSchema,
});

export const draftSaveResponseSchema = z.object({
  draft_id: z.string(),
  version: z.number().int(),
  content: draftContentSchema,
  updated_at: z.string(),
});

const draftListItemSchema = draftSaveResponseSchema.omit({ content: true }).extend({
  title: z.string(),
});

export const draftListResponseSchema = z.object({
  results: z.array(draftListItemSchema),
});

const draftResourceItemSchema = z.object({
  contractor_id: z.number(),
  contractor_name: z.string(),
  trade_id: z.number(),
  trade_name: z.string(),
  quantity: z.number(),
});

const draftResourceGroupSchema = z.object({
  total_quantity: z.number(),
  items: z.array(draftResourceItemSchema),
});

const draftDetailContentSchema = z
  .object({
    description: z.string(),
    location: z.string(),
    log_date: z.string(),
    weather: z.string(),
    temperature: z.string(),
    temperature_unit: z.string(),
    precipitation: z.string(),
    precipitation_unit: z.string(),
    process_rate: z.string(),
    tags: z.array(z.object({ id: z.number(), name: z.string() })),
    workforce: draftResourceGroupSchema,
    equipment: draftResourceGroupSchema,
    image_urls: z.array(z.string()),
  })
  .partial();

export const draftDetailContentToFormSchema = draftDetailContentSchema.transform(
  (content): Partial<DailyLogFormValues> => ({
    ...(content.description !== undefined && { description: content.description }),
    ...(content.location !== undefined && { location: content.location }),
    ...(content.log_date !== undefined && { date: content.log_date }),
    ...(content.weather !== undefined && { weather: content.weather }),
    ...(content.temperature !== undefined && { temperature: Number(content.temperature) }),
    ...(content.precipitation !== undefined && { precipitation: Number(content.precipitation) }),
    ...(content.process_rate !== undefined && { processRate: Number(content.process_rate) }),
    ...(content.tags !== undefined && { tags: content.tags }),
    ...(content.workforce !== undefined && {
      workforce_contractors: groupWorkforceItemsByContractor(content.workforce.items),
    }),
    ...(content.equipment !== undefined && {
      equipment_contractors: groupEquipmentItemsByContractor(content.equipment.items),
    }),
    ...(content.image_urls !== undefined && { image_urls: content.image_urls }),
  }),
);

export const draftDetailResponseSchema = z.object({
  draft_id: z.string(),
  version: z.number().int(),
  updated_at: z.string(),
  content: draftDetailContentSchema,
});

export type DailyLogDraft = z.infer<typeof dailyLogDraftSchema>;
export type DraftSaveRequest = z.infer<typeof draftSaveRequestSchema>;
export type DraftSaveResponse = z.infer<typeof draftSaveResponseSchema>;
export type DraftDetailResponse = z.infer<typeof draftDetailResponseSchema>;
export type DraftDetailContent = z.infer<typeof draftDetailContentSchema>;
export type DraftListItem = z.infer<typeof draftListItemSchema>;
export type DraftListResponse = z.infer<typeof draftListResponseSchema>;

export type LocalDraftRecord = DraftSaveResponse & { project_id: number };
